import { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "react-query";
import { Button, Flex, Spinner, Text } from "@radix-ui/themes";
import { Cross1Icon } from "@radix-ui/react-icons";
import { useEnrichmentApi } from "@/v2/entities/enrichment/Enrichment.api";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { DatabaseSync01 } from "@/v2/assets/icons/DatabaseSync01";
import styles from "@/v2/features/enrichment/enrichmentForm.module.scss";
import type { EnrichmentOption, ExtractionOutputField } from "@/v2/entities/enrichment/Enrichment.model";
import EnrichmentReviewForm from "@/v2/features/enrichment/EnrichmentReviewForm";
import type { EnrichmentType } from "@/v2/features/enrichment/EnrichmentReviewForm";
import { type TriggerType } from "@/v2/entities/enrichment/Enrichment.model";

function parseJsonArray(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // ignore
    }
  }
  return [];
}

function detectEnrichmentType(options: unknown): EnrichmentType {
  const parsedOptions = parseJsonArray(options);
  if (parsedOptions.length > 0) return "classification";
  return "extraction";
}

export default function EditEnrichmentPage() {
  const { enrichmentId } = useParams();
  const navigate = useNavigate();
  const enrichmentApi = useEnrichmentApi();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const syncEnrichmentsEnabled = useFlag("syncEnrichments");

  const { data: enrichment, isLoading } = useQuery({
    queryKey: ["enrichment", enrichmentId],
    queryFn: () => enrichmentApi.getById(enrichmentId || ""),
    enabled: !!enrichmentId,
  });

  const enrichmentType = useMemo<EnrichmentType>(() => {
    if (!enrichment) return "classification";
    return detectEnrichmentType(enrichment.options);
  }, [enrichment]);

  const initialOptions = useMemo<EnrichmentOption[]>(() => {
    if (!enrichment) return [];
    return parseJsonArray(enrichment.options).map((opt: any) => ({
      name: opt.name || "",
      value: opt.value || opt.name?.toLowerCase().replace(/\s+/g, "_") || "",
      brief: opt.brief || "",
    }));
  }, [enrichment]);

  const initialOutputFields = useMemo<ExtractionOutputField[]>(() => {
    if (!enrichment) return [];
    return parseJsonArray(enrichment.output).map((f: any) => ({
      name: f.name || "",
      key: f.key || f.name?.toLowerCase().replace(/\s+/g, "_") || "",
      brief: f.brief || "",
    }));
  }, [enrichment]);

  const [editedName, setEditedName] = useState("");
  const [editedTask, setEditedTask] = useState("");
  const [editedOptions, setEditedOptions] = useState<EnrichmentOption[]>([]);
  const [editedOutputFields, setEditedOutputFields] = useState<ExtractionOutputField[]>([]);
  const [editedTrigger, setEditedTrigger] = useState<TriggerType>("ON_FINITE_STATE");
  const [editedTopicIds, setEditedTopicIds] = useState<number[]>([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (enrichment && !initialized) {
      setEditedName(enrichment.name || "");
      setEditedTask(enrichment.task || "");
      setEditedOptions(initialOptions);
      setEditedOutputFields(initialOutputFields);
      setEditedTrigger((enrichment.trigger as TriggerType) || "ON_FINITE_STATE");
      setEditedTopicIds(enrichment.topicIds ?? []);
      setInitialized(true);
    }
  }, [enrichment, initialized, initialOptions, initialOutputFields]);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEditedName(e.target.value);
    },
    []
  );

  const handleTaskChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setEditedTask(e.target.value);
    },
    []
  );

  const handleTriggerChange = useCallback((value: string) => {
    setEditedTrigger(value as TriggerType);
  }, []);

  const handleCancel = useCallback(() => {
    navigate("/enrichments");
  }, [navigate]);

  const handleSyncClick = useCallback(() => {
    navigate(`/enrichments/${enrichmentId}/sync`);
  }, [navigate, enrichmentId]);

  const handleSubmit = useCallback(async () => {
    if (!enrichmentId || !enrichment || !editedName.trim()) return;

    setLoading(true);

    try {
      await enrichmentApi.update({
        id: enrichmentId,
        name: editedName.trim(),
        task: editedTask,
        filterRules: "{}",
        options: JSON.stringify(
          enrichmentType === "classification" ? editedOptions : []
        ),
        isEnabled: enrichment.isEnabled,
        trigger: editedTrigger,
        topicIds: editedTopicIds,
        output: JSON.stringify(
          enrichmentType === "extraction" ? editedOutputFields : []
        ),
        sources: enrichment.sources || [],
      });

      queryClient.invalidateQueries(["enrichment", enrichmentId]);
      toast("enrichment updated successfully");
      navigate("/enrichments");
    } catch {
      toast.error("Failed to update enrichment");
    } finally {
      setLoading(false);
    }
  }, [
    enrichmentApi,
    queryClient,
    navigate,
    enrichmentId,
    enrichment,
    editedName,
    editedTask,
    enrichmentType,
    editedOptions,
    editedOutputFields,
    editedTrigger,
    editedTopicIds,
  ]);

  if (isLoading) {
    return (
      <Flex className="w-full h-full items-center justify-center">
        <Spinner />
      </Flex>
    );
  }

  if (!enrichment) {
    return (
      <Flex className="w-full h-full items-center justify-center">
        <Text>Enrichment not found</Text>
      </Flex>
    );
  }

  return (
    <div className={styles.enrichmentFormContainer}>
      <div className={styles.enrichmentFormHeader}>
        <div className={styles.enrichmentFormHeaderLeft}>
          <button
            onClick={handleCancel}
            className={styles.enrichmentFormCloseButton}
            aria-label="Close"
            type="button"
          >
            <Cross1Icon
              style={{ width: "16px", height: "16px", color: "var(--gray-11)" }}
            />
          </button>
          <span className={styles.enrichmentFormHeaderTitle}>
            Edit Enrichment
          </span>
        </div>
        <div className={styles.enrichmentFormHeaderActions}>
          {syncEnrichmentsEnabled && (
            <Button variant="outline" highContrast onClick={handleSyncClick}>
              <DatabaseSync01 size={12} fill="gray" />
              Sync
            </Button>
          )}
        </div>
      </div>

      <div className={styles.enrichmentFormMainContent}>
        <EnrichmentReviewForm
          name={editedName}
          onNameChange={handleNameChange}
          task={editedTask}
          onTaskChange={handleTaskChange}
          enrichmentType={enrichmentType}
          options={editedOptions}
          onOptionsChange={setEditedOptions}
          trigger={editedTrigger}
          onTriggerChange={handleTriggerChange}
          topicIds={editedTopicIds}
          onTopicIdsChange={setEditedTopicIds}
          onSubmit={handleSubmit}
          loading={loading}
          submitLabel="Save"
        />
      </div>
    </div>
  );
}
